import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { motion } from 'framer-motion';
import PromptCard from './PromptCard';

// Create sliding animations in opposite directions
const slideLeftToRight = keyframes`
  from { transform: translateX(-50%); }
  to { transform: translateX(0%); }
`;

const slideRightToLeft = keyframes`
  from { transform: translateX(0%); }
  to { transform: translateX(-50%); }
`;

// Variantes de animación para la aparición suave
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
      staggerChildren: 0.15
    }
  }
};

const rowVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const SlideTrack = styled(Box)(({ theme, direction }) => ({
  display: 'flex',
  width: 'fit-content',
  animation: `${direction === 'ltr' ? slideLeftToRight : slideRightToLeft} 60s linear infinite`,
  '&:hover': {
    animationPlayState: 'paused'
  }
}));

const SlideContainer = styled(Box)(({ theme }) => ({
  width: '100vw',
  overflow: 'hidden',
  marginBottom: theme.spacing(1.5),
  padding: 0,
  // Mejorar el posicionamiento para ocupar todo el ancho
  position: 'relative',
  left: '50%',
  transform: 'translateX(-50%)',
  boxSizing: 'border-box',
}));

const SlidingPromptCards = ({ 
  cards, 
  handleCardClick, 
  isPro, 
  selectedCard,
  interfaceStyle
}) => {
  
  // Usar useMemo para que las tarjetas no cambien en cada renderizado
  const { topRowCards, bottomRowCards } = useMemo(() => {
    // Crear una función de mezcla determinista usando el array original como semilla
    const deterministicShuffle = (array) => {
      // Crear un hash simple basado en los textos de las tarjetas para usar como semilla
      const seed = array.reduce((acc, card, i) => 
        acc + (card.text?.charCodeAt(0) || 0) * (i + 1), 0);
      
      // Función pseudo-aleatoria determinista
      const random = (max) => {
        const x = Math.sin(seed * 9999) * 10000;
        return Math.floor((x - Math.floor(x)) * max);
      };
      
      // Mezclar de manera determinista
      const result = [...array];
      for (let i = result.length - 1; i > 0; i--) {
        const j = random(i + 1) % (i + 1);
        [result[i], result[j]] = [result[j], result[i]];
      }
      return result;
    };
    
    // Mezclar las tarjetas de forma determinista
    const shuffled = deterministicShuffle(cards);
    
    // Dividir en dos filas
    return {
      topRowCards: shuffled.slice(0, 8),
      bottomRowCards: shuffled.slice(8, 16)
    };
  }, [cards]); // Solo depende del array de cards
  
  // Duplicate cards to create seamless loop
  const duplicatedTopCards = [...topRowCards, ...topRowCards];
  const duplicatedBottomCards = [...bottomRowCards, ...bottomRowCards];

  return (
    <Box
      component={motion.div}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      sx={{
        width: '100%',
        mt: 2,
        mb: 1,
        position: 'relative',
        overflow: 'visible',
        padding: 0
      }}
    >
      {/* Top row - slides left to right */}
      <Box component={motion.div} variants={rowVariants}>
        <SlideContainer>
          <SlideTrack direction="ltr">
            {duplicatedTopCards.map((card, index) => (
              <Box key={`top-${index}`} sx={{ px: 0.5 }}>
                <PromptCard
                  Icon={card.Icon}
                  text={card.text}
                action="sliding"
                isProOnly={card.isProOnly}
                isPro={isPro}
                onClick={handleCardClick}
                selected={selectedCard === card.text}
                interfaceStyle={interfaceStyle}
              />
            </Box>
          ))}
        </SlideTrack>
      </SlideContainer>
      </Box>

      {/* Bottom row - slides right to left */}
      <Box component={motion.div} variants={rowVariants}>
        <SlideContainer>
          <SlideTrack direction="rtl">
            {duplicatedBottomCards.map((card, index) => (
              <Box key={`bottom-${index}`} sx={{ px: 0.5 }}>
                <PromptCard
                  Icon={card.Icon}
                  text={card.text}
                  action="sliding"
                  isProOnly={card.isProOnly}
                  isPro={isPro}
                  onClick={handleCardClick}
                  selected={selectedCard === card.text}
                  interfaceStyle={interfaceStyle}
                />
              </Box>
            ))}
          </SlideTrack>
        </SlideContainer>
      </Box>
    </Box>
  );
};

SlidingPromptCards.propTypes = {
  cards: PropTypes.array.isRequired,
  handleCardClick: PropTypes.func.isRequired,
  isPro: PropTypes.bool,
  selectedCard: PropTypes.string,
  interfaceStyle: PropTypes.string.isRequired,
};

export default React.memo(SlidingPromptCards);